import { addDays, addMonths, addWeeks, compareAsc, eachDayOfInterval, endOfMonth, format, getDaysInMonth, isValid, parseISO, startOfMonth, } from 'date-fns';
const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const INVALID_DATE_MESSAGE = 'Invalid date value';
const LONG_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
});
const SHORT_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
    month: 'short',
});
/** Parse a strict `YYYY-MM-DD` calendar date at local midnight. */
export function parseYmd(value) {
    if (!YMD_PATTERN.test(value))
        throw new RangeError(INVALID_DATE_MESSAGE);
    const parsed = parseISO(value);
    if (!isValid(parsed) || format(parsed, 'yyyy-MM-dd') !== value) {
        throw new RangeError(INVALID_DATE_MESSAGE);
    }
    return parsed;
}
/** Serialize a valid local `Date` as a `YYYY-MM-DD` calendar date. */
export function ymd(date) {
    if (!isValid(date))
        throw new RangeError(INVALID_DATE_MESSAGE);
    const year = date.getFullYear();
    if (year < 1 || year > 9999)
        throw new RangeError(INVALID_DATE_MESSAGE);
    return format(date, 'yyyy-MM-dd');
}
/** Shift a calendar date by a number of local calendar days. */
export function shiftDays(value, amount) {
    return ymd(addDays(parseYmd(value), amount));
}
/** Shift a calendar date by a number of local calendar weeks. */
export function shiftWeeks(value, amount) {
    return ymd(addWeeks(parseYmd(value), amount));
}
/** Shift a calendar date by months, clamping to the target month's final day. */
export function shiftMonths(value, amount) {
    return ymd(addMonths(parseYmd(value), amount));
}
/** Return the first and last calendar dates in the anchor's month. */
export function monthRange(anchor) {
    const date = parseYmd(anchor);
    return { from: ymd(startOfMonth(date)), to: ymd(endOfMonth(date)) };
}
/** Return the first calendar date in the anchor's month. */
export function firstOfMonth(anchor) {
    return ymd(startOfMonth(parseYmd(anchor)));
}
/** Return the number of calendar days in the anchor's month. */
export function daysInMonth(anchor) {
    return getDaysInMonth(parseYmd(anchor));
}
/** Return an inclusive list of calendar dates, or an empty list for a reversed interval. */
export function daysBetween(from, to) {
    const start = parseYmd(from);
    const end = parseYmd(to);
    if (compareAsc(start, end) > 0)
        return [];
    return eachDayOfInterval({ start, end }).map(ymd);
}
/** Compare two validated calendar dates, returning a negative number, zero, or a positive number. */
export function compareYmd(left, right) {
    return compareAsc(parseYmd(left), parseYmd(right));
}
/** Render the anchor's month and year in long-form US English, such as `April 2026`. */
export function humanMonthLong(anchor) {
    return LONG_MONTH_FORMATTER.format(parseYmd(anchor));
}
/** Render the anchor's abbreviated month in US English, such as `Apr`. */
export function humanMonthShort(anchor) {
    return SHORT_MONTH_FORMATTER.format(parseYmd(anchor));
}
/** Parse a validated `YYYY-MM-DD` calendar date at UTC midnight. */
function utcDateFromYmd(value) {
    parseYmd(value);
    const [year, month, day] = value.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}
/** Serialize a UTC-midnight `Date` as a `YYYY-MM-DD` calendar date. */
function ymdFromUtcDate(date) {
    return date.toISOString().slice(0, 10);
}
/** Whether the UTC-parsed calendar date falls Monday through Friday. */
export function isWeekday(value) {
    const day = utcDateFromYmd(value).getUTCDay();
    return day >= 1 && day <= 5;
}
/** Inclusive walk from `from` to `to`, keeping only weekday calendar dates. */
export function workdaysBetween(from, to) {
    const dates = [];
    const cursor = utcDateFromYmd(from);
    const last = utcDateFromYmd(to);
    while (cursor <= last) {
        const day = cursor.getUTCDay();
        if (day >= 1 && day <= 5)
            dates.push(ymdFromUtcDate(cursor));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
}
/** Return the Monday of the ISO week containing `value` (Sunday's Monday is 6 days back). */
/** ISO day of the week of a `YYYY-MM-DD` calendar date: 1 = Monday … 7 = Sunday. */
export function isoDayOf(value) {
    return utcDateFromYmd(value).getUTCDay() || 7;
}
export function mondayOf(value) {
    const date = utcDateFromYmd(value);
    const day = date.getUTCDay();
    date.setUTCDate(date.getUTCDate() - ((day + 6) % 7));
    return ymdFromUtcDate(date);
}
/** Return the `YYYY-Www` ISO week id for the week containing `value`. */
export function isoWeekOf(value) {
    const monday = utcDateFromYmd(mondayOf(value));
    const thursday = new Date(monday);
    thursday.setUTCDate(monday.getUTCDate() + 3);
    const isoYear = thursday.getUTCFullYear();
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const jan4IsoDay = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (jan4IsoDay - 1));
    const week = Math.floor((monday.getTime() - week1Monday.getTime()) / 604_800_000) + 1;
    return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
/** Render an instant as a `YYYY-MM-DD` calendar date in the given IANA timezone. */
export function ymdInTimezone(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const value = (type) => parts.find((part) => part.type === type)?.value ?? '';
    return `${value('year')}-${value('month')}-${value('day')}`;
}
